/**
 * Pagina 404 — agganciata al router come notFoundComponent.
 * Suggerisce route alternative basate su fuzzy match dell'URL digitato.
 */
import { Link, useLocation } from '@tanstack/react-router';
import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBriefcase,
  IconChartBar,
  IconCompass,
  IconHelp,
  IconHome,
  IconMoodSad,
  IconSearch,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';

interface SuggestedRoute {
  to: string;
  label: string;
  keywords: string[];
}

const ALL_ROUTES: SuggestedRoute[] = [
  { to: '/', label: 'Home', keywords: ['home', 'dashboard'] },
  { to: '/tasks', label: 'Task', keywords: ['tasks', 'attività', 'todo'] },
  { to: '/tasks/kanban', label: 'Kanban', keywords: ['kanban', 'board'] },
  { to: '/tasks/gantt', label: 'Gantt', keywords: ['gantt', 'timeline'] },
  { to: '/timesheet', label: 'Timesheet', keywords: ['ore', 'timesheet', 'rendicontazione'] },
  { to: '/projects', label: 'Commesse', keywords: ['projects', 'commesse', 'progetti'] },
  { to: '/planning', label: 'Pianificazione', keywords: ['planning', 'plan'] },
  { to: '/attendance', label: 'Presenze', keywords: ['attendance', 'presenze', 'timbrature'] },
  { to: '/leave', label: 'Ferie / Permessi', keywords: ['leave', 'ferie', 'permessi', 'assenze'] },
  { to: '/smart-working', label: 'Smart working', keywords: ['smart', 'sw', 'remote'] },
  { to: '/workforce', label: 'Workforce', keywords: ['workforce', 'team'] },
  { to: '/vehicles', label: 'Mezzi', keywords: ['vehicles', 'mezzi', 'auto'] },
  { to: '/insights', label: 'Insights', keywords: ['insights', 'analytics'] },
  { to: '/statistics', label: 'Statistiche', keywords: ['statistics', 'stats'] },
  { to: '/reporting', label: 'Reporting', keywords: ['reporting', 'report'] },
  { to: '/articles', label: 'Articoli & guide', keywords: ['articles', 'knowledge', 'guide'] },
  { to: '/help', label: 'Aiuto', keywords: ['help', 'aiuto', 'faq'] },
  { to: '/tickets', label: 'Help desk', keywords: ['tickets', 'help desk', 'supporto'] },
  { to: '/users', label: 'Utenti', keywords: ['users', 'utenti'] },
  { to: '/roles', label: 'Ruoli', keywords: ['roles', 'ruoli', 'permessi'] },
  { to: '/settings', label: 'Impostazioni', keywords: ['settings', 'preferences', 'impostazioni'] },
];

const QUICK_LINKS = [
  { to: '/', label: 'Home', icon: IconHome, color: 'blue' },
  { to: '/projects', label: 'Commesse', icon: IconBriefcase, color: 'green' },
  { to: '/insights', label: 'Insights', icon: IconChartBar, color: 'teal' },
  { to: '/help', label: 'Aiuto', icon: IconHelp, color: 'orange' },
];

function suggestionsFor(input: string): SuggestedRoute[] {
  if (!input) return [];
  const fuse = new Fuse(ALL_ROUTES, {
    keys: ['label', 'keywords', 'to'],
    threshold: 0.4,
    includeScore: true,
  });
  const results = fuse.search(input.replace(/^\//, '').replace(/[-_/]/g, ' '));
  return results.slice(0, 4).map((r) => r.item);
}

export function NotFoundPage() {
  const loc = useLocation();
  const [query, setQuery] = useState('');

  const autoSuggestions = useMemo(() => suggestionsFor(loc.pathname), [loc.pathname]);
  const querySuggestions = useMemo(() => suggestionsFor(query), [query]);
  const suggestions = query.trim() ? querySuggestions : autoSuggestions;

  return (
    <Center mih="100vh">
      <Container size="md" w="100%" py="xl">
        <Stack align="center" gap="lg" ta="center">
          <ThemeIcon size={96} radius="50%" variant="light" color="blue">
            <IconMoodSad size={56} />
          </ThemeIcon>
          <Title order={1} size={64}>404</Title>
          <Title order={2} fw={500}>Pagina non trovata</Title>
          <Text c="dimmed" maw={520}>
            La pagina <Badge variant="light" size="lg" style={{ fontFamily: 'monospace' }}>{loc.pathname}</Badge> non esiste,
            è stata spostata o non hai i permessi per visualizzarla.
          </Text>

          {suggestions.length > 0 && (
            <Card withBorder padding="md" radius="md" maw={520} w="100%">
              <Group gap={6} mb="xs" justify="center">
                <IconCompass size={16} />
                <Text size="sm" fw={500}>
                  {query.trim() ? 'Risultati per' : 'Forse cercavi'}{' '}
                  {query.trim() && <Badge variant="light">{query}</Badge>}
                </Text>
              </Group>
              <Stack gap={4}>
                {suggestions.map((s) => (
                  <Anchor key={s.to} component={Link} to={s.to} underline="never">
                    <Card withBorder padding="xs" radius="sm">
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>{s.label}</Text>
                        <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{s.to}</Text>
                      </Group>
                    </Card>
                  </Anchor>
                ))}
              </Stack>
            </Card>
          )}

          <TextInput
            placeholder="Cerca una pagina (es. ferie, kanban, timesheet)…"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            w={420}
            maw="100%"
          />

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs" maw={520} w="100%">
            {QUICK_LINKS.map((q) => (
              <Card key={q.to} withBorder padding="sm" radius="md" component={Link} to={q.to} style={{ textDecoration: 'none' }}>
                <Stack align="center" gap={4}>
                  <ThemeIcon variant="light" color={q.color} radius="md">
                    <q.icon size={18} />
                  </ThemeIcon>
                  <Text size="xs" c="var(--mantine-color-text)">{q.label}</Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          <Group>
            <Button
              component={Link}
              to="/"
              leftSection={<IconHome size={18} />}
              variant="filled"
            >
              Torna alla home
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={18} />}
              onClick={() => window.history.back()}
            >
              Indietro
            </Button>
          </Group>
        </Stack>
      </Container>
    </Center>
  );
}
